'use client';
import { mapProductsToCards, resolveClientProductCardData } from '../../../../product/index';
import { GtmListIdType } from '@vs-shop/schemas/gtm/list-types';
import { Container } from '@vs-shop/ui/container';
import ProductCardSkeleton from '../../../server/product/card/ProductCardSkeleton';
import { clsxm } from '@vs-shop/utils';
import { useIntl } from 'react-intl';

import {
  homepageProductsInitialPage,
  homepageProductsItemsPerPage,
} from '../../../../config/homepage';

import ProductCard from '../../../server/product/card/ProductCard';
import ProductCardButtons from '../../../server/product/card/ProductCardButtons';
import ProductCardHeader from '../../../server/product/card/ProductCardHeader';
import ProductCardImage from '../../../server/product/card/ProductCardImage';
import ProductCardMediaLink from '../../../server/product/card/ProductCardMediaLink';
import ProductCardPriceStock from '../../../server/product/card/ProductCardPriceStock';
import ProductCardStickers from '../../../server/product/card/ProductCardStickers';
import ProductCardTracker from '../../../server/product/card/ProductCardTracker';
import ProductCardCoupon from '../../../server/product/ProductCardCoupon';

import { fetchHomepageProductsPageAtom } from '../../../../effect/atom/products/fetchHomepageProductsPage';
import { ProductCardVariant } from '@vs-shop/commerce-ui/productCard';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import { useAtomValue } from '@effect/atom-react';

type Props = {
  taxonCode: string;
};

export default function HomepageProductsListing({ taxonCode }: Props) {
  const intl = useIntl();

  const productsResponse = useAtomValue(
    fetchHomepageProductsPageAtom(
      taxonCode,
      homepageProductsInitialPage,
      homepageProductsItemsPerPage
    )
  );

  return AsyncResult.builder(productsResponse)
    .onInitial(() => {
      return (
        <div>
          <Container classNameWrapper="bg-background-intense">
            <ul
              className="grid gap-0.5 tab:grid-cols-2 lap:grid-cols-4"
              data-slot="homepage-products-grid-loading"
            >
              {Array.from({ length: homepageProductsItemsPerPage }, (_, index) => (
                <li
                  key={`skeleton-${String(index)}`}
                  className={clsxm({
                    'hidden tab:block': index > 4,
                  })}
                >
                  <ProductCardSkeleton variant={ProductCardVariant.BASE} />
                </li>
              ))}
            </ul>
          </Container>
        </div>
      );
    })
    .onSuccess((success) => {
      const totalMembers = success['hydra:totalItems'];

      if (totalMembers === 0) {
        return (
          <Container classNameWrapper="bg-background-intense">
            <div
              className="flex items-center justify-center p-4 text-xl"
              data-slot="homepage-products-empty"
            >
              {intl.formatMessage({
                id: 'component.client.page.homepage.homepageProductsListing.noProductsFound',
                defaultMessage: 'Nenalezeny žádné produkty',
              })}
            </div>
          </Container>
        );
      }

      const productMembers = success['hydra:member'];
      const products = mapProductsToCards(productMembers);

      return (
        <div>
          <Container classNameWrapper="bg-background-intense">
            <ul
              className="grid gap-0.5 tab:grid-cols-2 lap:grid-cols-4"
              data-slot="homepage-products-grid"
            >
              {products.map((product, index) => {
                return (
                  <li
                    key={product.slug}
                    className={clsxm({
                      'hidden tab:block': index > 4,
                    })}
                  >
                    <ProductCardTracker
                      listType={GtmListIdType.HOMEPAGE_PRODUCTS}
                      product={resolveClientProductCardData(product)}
                    >
                      <ProductCard
                        variant={ProductCardVariant.BASE}
                        header={
                          <ProductCardHeader
                            productName={product.name}
                            routerUrl={product.routerUrl}
                            metaTitle={product.metaTitle}
                          />
                        }
                        media={
                          <ProductCardMediaLink
                            routerUrl={product.routerUrl}
                            metaTitle={product.metaTitle}
                            variant={ProductCardVariant.BASE}
                          >
                            <ProductCardImage
                              imagePath={product.imagePath}
                              alt={product.name}
                              variant={ProductCardVariant.BASE}
                            />
                            <ProductCardStickers
                              labels={product.labels}
                              ageRestriction={product.ageRestriction}
                              price={product.price}
                              originalPrice={product.originalPrice}
                            />
                          </ProductCardMediaLink>
                        }
                        promo={
                          <ProductCardCoupon
                            productCode={product.code}
                            productName={product.name}
                            discountBanner={product.discountBanner}
                            price={product.price}
                            priceWithoutTax={product.priceWithoutTax}
                            stock={product.stock}
                          />
                        }
                        description={product.subtitle ?? product.description}
                        footer={
                          <>
                            <ProductCardPriceStock
                              price={product.price}
                              originalPrice={product.originalPrice}
                              stock={product.stock}
                              labels={product.labels}
                              variant={ProductCardVariant.BASE}
                            />
                            <ProductCardButtons
                              productCode={product.code}
                              productName={product.name}
                              isFavourite={product.isFavourite}
                              routerUrl={product.routerUrl}
                              metaTitle={product.metaTitle}
                              variant={ProductCardVariant.BASE}
                            />
                          </>
                        }
                      />
                    </ProductCardTracker>
                  </li>
                );
              })}
            </ul>
          </Container>
        </div>
      );
    })
    .render();
}
